import { createRootRouteWithContext, createRoute, createRouter, Navigate, Outlet, useRouterState } from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import { AppShell } from "./components/AppShell";
import { LoginPage } from "./features/auth/LoginPage";
import { SetupPage } from "./features/auth/SetupPage";
import { HomePage } from "./features/home/HomePage";
import { ContactsListPage } from "./features/contacts/ContactsListPage";
import { parseContactsSearch } from "./features/contacts/listSearch";
import { ContactPage, type ContactView } from "./features/contacts/ContactPage";
import { DuplicatesPage } from "./features/contacts/DuplicatesPage";
import { ReportsListPage } from "./features/email/ReportsListPage";
import { ReportDetailPage } from "./features/email/ReportDetailPage";
import { ComparePage } from "./features/email/ComparePage";
import { BroadcastsPage } from "./features/email/BroadcastsPage";
import { BroadcastEditorPage } from "./features/email/BroadcastEditorPage";
import { SegmentsPage } from "./features/email/SegmentsPage";
import { SuppressionsPage } from "./features/email/SuppressionsPage";
import { WarmupPage } from "./features/email/WarmupPage";
import { GmailLogPage } from "./features/email/GmailLogPage";
import { SegmentEditorPage } from "./features/email/SegmentEditorPage";
import { SettingsPage } from "./features/settings/SettingsPage";
import { AutomationsPage } from "./features/automations/AutomationsPage";
import { AutomationDetailPage } from "./features/automations/AutomationDetailPage";
import { KeapCampaignsPage } from "./features/automations/KeapCampaignsPage";
import { RunPage } from "./features/automations/RunPage";
import { EventsPage } from "./features/events/EventsPage";
import { EventDetailPage } from "./features/events/EventDetailPage";
import { WebinarFunnelPage, type FunnelSearch } from "./features/reports/WebinarFunnelPage";
import { SavedReportsPage, type SavedReportsSearch } from "./features/reports/SavedReportsPage";
import { FormsPage } from "./features/forms/FormsPage";
import { useMe } from "./lib/session";
import { Skeleton } from "./components/ui/skeleton";
import { ApprovalsPage } from "@/features/billing/ApprovalsPage";
import { MoneyApprovalPage } from "@/features/billing/MoneyApprovals";
import { ProductsPage } from "@/features/billing/ProductsPage";
import { ProductDetailPage } from "@/features/billing/ProductDetailPage";
import { PromotionsPage } from "@/features/billing/PromotionsPage";
import { SubscriptionsPage } from "@/features/billing/SubscriptionsPage";
import { SubscriptionDetailPage } from "@/features/billing/SubscriptionDetailPage";
import { OrdersPage } from "@/features/billing/OrdersPage";
import { OrderDetailPage } from "@/features/billing/OrderDetailPage";
import { DeliveryProblemsPage } from "@/features/billing/DeliveryProblemsPage";
import { CardLinksPage } from "@/features/billing/CardLinksPage";
import { ClubCardsPage } from "@/features/billing/ClubCardsPage";
import { AffiliatesPage, type AffiliatesTab } from "@/features/affiliates/AffiliatesPage";
import { AffiliateDetailPage } from "@/features/affiliates/AffiliateDetailPage";
import { BillingRunsPage } from "@/features/billing/BillingRunsPage";
import { PartnersToPayPage } from "@/features/affiliates/PartnersToPayPage";

function Root() {
  const me = useMe();
  const path = useRouterState({ select: (s) => s.location.pathname });
  if (path === "/login" || path === "/setup") return <Outlet />; // no session needed
  if (me.isLoading) {
    return (
      <div className="p-8">
        <Skeleton className="h-8 w-64" />
      </div>
    );
  }
  if (!me.data) return <Navigate to="/login" search={{ next: path }} />;
  return (
    <AppShell me={me.data}>
      <Outlet />
    </AppShell>
  );
}

const str = (v: unknown) => (typeof v === "string" && v ? v : undefined);
const num = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? v : typeof v === "string" && /^\d+$/.test(v) ? Number(v) : undefined);

const rootRoute = createRootRouteWithContext<{ queryClient: QueryClient }>()({ component: Root });

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  validateSearch: (s: Record<string, unknown>) => ({ next: typeof s.next === "string" ? s.next : "/" }),
  component: LoginPage,
});
// An invite link (users_invite): <base>/setup#<token>.
const setupRoute = createRoute({ getParentRoute: () => rootRoute, path: "/setup", component: SetupPage });
const homeRoute = createRoute({ getParentRoute: () => rootRoute, path: "/", component: HomePage });

// ---- contacts (crm-admin-ui) ----
const contactsRoute = createRoute({ getParentRoute: () => rootRoute, path: "/contacts", validateSearch: parseContactsSearch, component: ContactsListPage });
const duplicatesRoute = createRoute({ getParentRoute: () => rootRoute, path: "/contacts/duplicates", component: DuplicatesPage });
export const contactRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/contacts/$contactId",
  validateSearch: (s: Record<string, unknown>): { view?: ContactView } => ({
    view: (["billing", "email", "files"] as const).find((v) => v === s.view),
  }),
  component: ContactPage,
});

// ---- email: reports, broadcasts, segments (crm-admin-ui screens for crm-email's operations) ----
const reportsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/email/reports",
  validateSearch: (s: Record<string, unknown>): { brand?: number; kind?: string; since?: string; until?: string; segment?: number } => ({
    brand: num(s.brand),
    kind: str(s.kind),
    since: str(s.since),
    until: str(s.until),
    segment: num(s.segment),
  }),
  component: ReportsListPage,
});
const compareRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/email/reports/compare",
  validateSearch: (s: Record<string, unknown>): { ids?: number[] } => ({
    ids: (Array.isArray(s.ids) ? s.ids : typeof s.ids === "string" ? s.ids.split(",") : []).map(Number).filter((n) => n > 0) as number[],
  }),
  component: ComparePage,
});
export const reportRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/email/reports/$reportId",
  validateSearch: (s: Record<string, unknown>): { list?: string; link?: string } => ({ list: str(s.list), link: str(s.link) }),
  component: ReportDetailPage,
});
const broadcastsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/email/broadcasts",
  // pause: from ⌘K "Pause all email sending" (opens the pause confirm).
  validateSearch: (s: Record<string, unknown>): { pause?: boolean } => ({ pause: s.pause === true || s.pause === "true" || s.pause === "1" || s.pause === 1 ? true : undefined }),
  component: BroadcastsPage,
});
export const broadcastRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/email/broadcasts/$broadcastId",
  validateSearch: (s: Record<string, unknown>): { segment?: number; brand?: number } => ({ segment: num(s.segment), brand: num(s.brand) }),
  component: BroadcastEditorPage,
});
const segmentsRoute = createRoute({ getParentRoute: () => rootRoute, path: "/email/segments", component: SegmentsPage });
const suppressionsRoute = createRoute({ getParentRoute: () => rootRoute, path: "/email/suppressions", component: SuppressionsPage });
const warmupRoute = createRoute({ getParentRoute: () => rootRoute, path: "/email/warmup", component: WarmupPage });
// crm-integrations: Brandon's one-to-one Gmail on timelines, and Sync Gmail now.
const gmailRoute = createRoute({ getParentRoute: () => rootRoute, path: "/email/gmail", component: GmailLogPage });
export const segmentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/email/segments/$segmentId",
  validateSearch: (s: Record<string, unknown>): { from_search?: number } => ({ from_search: num(s.from_search) }),
  component: SegmentEditorPage,
});

// ---- automations (crm-automations' operations) ----
const automationsRoute = createRoute({ getParentRoute: () => rootRoute, path: "/automations", component: AutomationsPage });
export const automationRoute = createRoute({ getParentRoute: () => rootRoute, path: "/automations/$automationId", component: AutomationDetailPage });
// crm-automations: the rebuilt Keap campaigns and one person's run.
const automationKeapRoute = createRoute({ getParentRoute: () => rootRoute, path: "/automations/keap", component: KeapCampaignsPage });
const automationRunRoute = createRoute({ getParentRoute: () => rootRoute, path: "/automations/runs/$runId", component: RunPage });

// ---- events and forms (crm-automations' operations) ----
const eventsRoute = createRoute({ getParentRoute: () => rootRoute, path: "/events", component: EventsPage });
export const eventRoute = createRoute({ getParentRoute: () => rootRoute, path: "/events/$eventId", component: EventDetailPage });
// crm-integrations: what an event's registrants bought afterwards (reports_webinar_funnel).
const eventSalesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/events/sales",
  validateSearch: (s: Record<string, unknown>): FunnelSearch => ({ event: num(s.event), session: num(s.session), days: num(s.days), product: str(s.product) }),
  component: WebinarFunnelPage,
});
// crm-integrations: saved reports (Brandon's Keap saved searches), run for any dates and contact list.
const savedReportsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reports",
  validateSearch: (s: Record<string, unknown>): SavedReportsSearch => ({
    report: num(s.report),
    due: s.due === "upcoming" || s.due === "past_due" ? s.due : undefined,
  }),
  component: SavedReportsPage,
});
const formsRoute = createRoute({ getParentRoute: () => rootRoute, path: "/forms", component: FormsPage });

// ---- billing (crm-billing) ----
const approvalsRoute = createRoute({ getParentRoute: () => rootRoute, path: "/billing/approvals", component: ApprovalsPage });
const moneyApprovalRoute = createRoute({ getParentRoute: () => rootRoute, path: "/billing/money-approvals/$approvalId", component: MoneyApprovalPage });
const productsRoute = createRoute({ getParentRoute: () => rootRoute, path: "/billing/products", component: ProductsPage });
const productRoute = createRoute({ getParentRoute: () => rootRoute, path: "/billing/products/$productId", component: ProductDetailPage });
const promotionsRoute = createRoute({ getParentRoute: () => rootRoute, path: "/billing/promotions", component: PromotionsPage });
const subscriptionsRoute = createRoute({ getParentRoute: () => rootRoute, path: "/billing/subscriptions", component: SubscriptionsPage });
const subscriptionRoute = createRoute({ getParentRoute: () => rootRoute, path: "/billing/subscriptions/$subscriptionId", component: SubscriptionDetailPage });
const ordersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/billing/orders",
  validateSearch: (s: Record<string, unknown>): { tab?: "payments" } => ({ tab: s.tab === "payments" ? "payments" : undefined }),
  component: OrdersPage,
});
const orderRoute = createRoute({ getParentRoute: () => rootRoute, path: "/billing/orders/$orderId", component: OrderDetailPage });
const deliveryProblemsRoute = createRoute({ getParentRoute: () => rootRoute, path: "/billing/delivery-problems", component: DeliveryProblemsPage });
const cardLinksRoute = createRoute({ getParentRoute: () => rootRoute, path: "/billing/card-links", component: CardLinksPage });
const clubCardsRoute = createRoute({ getParentRoute: () => rootRoute, path: "/billing/club-cards", component: ClubCardsPage });
const billingRunsRoute = createRoute({ getParentRoute: () => rootRoute, path: "/billing/runs", component: BillingRunsPage });
const partnersToPayRoute = createRoute({ getParentRoute: () => rootRoute, path: "/billing/partners", component: PartnersToPayPage });

// ---- affiliates (crm-billing's affiliates module) ----
const AFFILIATE_TABS: AffiliatesTab[] = ["commissions", "payouts", "programs"];
const affiliatesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/affiliates",
  validateSearch: (s: Record<string, unknown>): { tab?: AffiliatesTab } => ({ tab: AFFILIATE_TABS.find((t) => t === s.tab) }),
  component: AffiliatesPage,
});
const affiliateRoute = createRoute({ getParentRoute: () => rootRoute, path: "/affiliates/$affiliateId", component: AffiliateDetailPage });

// ---- settings ----
const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings",
  validateSearch: (s: Record<string, unknown>): { tab?: string } => ({ tab: str(s.tab) }),
  component: SettingsPage,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  setupRoute,
  homeRoute,
  contactsRoute,
  duplicatesRoute,
  contactRoute,
  reportsRoute,
  compareRoute,
  reportRoute,
  broadcastsRoute,
  broadcastRoute,
  segmentsRoute,
  suppressionsRoute,
  warmupRoute,
  gmailRoute,
  segmentRoute,
  automationsRoute,
  automationRoute,
  automationKeapRoute,
  automationRunRoute,
  eventsRoute,
  eventSalesRoute,
  eventRoute,
  formsRoute,
  savedReportsRoute,
  settingsRoute,
  approvalsRoute,
  moneyApprovalRoute,
  deliveryProblemsRoute,
  productsRoute,
  productRoute,
  promotionsRoute,
  subscriptionsRoute,
  subscriptionRoute,
  ordersRoute,
  orderRoute,
  cardLinksRoute,
  clubCardsRoute,
  affiliatesRoute,
  affiliateRoute,
  billingRunsRoute,
  partnersToPayRoute,
]);

export const router = createRouter({ routeTree, context: { queryClient: undefined! }, defaultPreload: "intent" });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
